import React, { useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import ErrorMsg from "../errorMsg/ErrorMsg";
import TTab from "../tab/Tab";
import {
  files,
  options,
  setLocalData,
} from "../../utils/global";
import Lottie from "react-lottie";
import successLottie from "../../assets/lottie/success.json";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import copyImage from "../../assets/copy.png";
import copyFormatImage from "../../assets/copy-format.png";
import UploadIcon from "../../assets/upload.png";
import * as prettier from "prettier";
import htmlParser from "prettier/plugins/html";

import "./sandbox.css";

const Sandbox = ({ vpWidth, activeTab }) => {
  const [openedEditor, setOpenedEditor] = useState("html");
  const [fileName, setFileName] = useState("index.html");
  const editorRef = useRef(null);
  const iframeRef = useRef(null);
  const [srcDoc, setSrcDoc] = useState("");
  const [htmlErrors, setHtmlErrors] = useState([]);
  const [cssErrors, setCssErrors] = useState([]);
  const [jsErrors, setJsErrors] = useState([]);
  const [copyMsg, setCopyMsg] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [formattedHtml, setFormattedHtml] = useState("");
  const isTestHtml = activeTab === "testHtml";
  const isValidateHtml = activeTab === "validateHtml";

  useEffect(() => {
    if (openedEditor === "html") {
      setFileName("index.html");
    } else if (openedEditor === "css") {
      setFileName("style.css");
    } else {
      setFileName("script.js");
    }
  }, [openedEditor]);

  useEffect(() => {
    setSrcDoc(`
        <html>
          <script>${files["script.js"].value}</script>
          <body>${files["index.html"].value}</body>
          <style>${files["style.css"].value}</style>
        </html>
      `);
    formatHtml(files["index.html"].value);
  }, []);

  function handleEditorDidMount(editor) {
    editorRef.current = editor;
  }

  const formatHtml = async (htmlCode) => {
    try {
      const formatted = await prettier.format(htmlCode, {
        parser: "html",
        plugins: [htmlParser],
      });
      setFormattedHtml(formatted);
    } catch (error) {
      console.error("Error formatting HTML:", error);
    }
  };

  const validateHTML = async (htmlCode) => {
    try {
      const response = await fetch(
        "https://validator.w3.org/nu/?out=json&level=error",
        {
          method: "POST",
          headers: {
            "Content-Type": "text/html; charset=utf-8",
          },
          body: htmlCode,
        }
      );

      const validationData = await response.json();

      const validationErrors = validationData.messages
        .filter((message) => message.type === "error")
        .map((message) => ({
          message: message.message,
          lineNumber: message.lastLine,
        }));

      return validationErrors;
    } catch (error) {
      console.error("HTML validation failed:", error);
      return [];
    }
  };

  function handleEditorValidation(markers) {
    if (openedEditor === "html") {
    } else if (openedEditor === "css") {
      setCssErrors([...markers]);
    } else {
      setJsErrors([...markers]);
    }
  }

  function handleIframeLoad() {
    let iframeDocument = iframeRef.current.contentDocument;
    setPageTitle(iframeDocument.title);
  }

  const copyFunc = () => {
    if (openedEditor === "html") {
      setCopyMsg("Copied HTML Code.");
      navigator.clipboard.writeText(files["index.html"].value);
    } else if (openedEditor === "css") {
      setCopyMsg("Copied CSS Code.");
      navigator.clipboard.writeText(files["style.css"].value);
    } else {
      setCopyMsg("Copied JavaScript Code.");
      navigator.clipboard.writeText(files["script.js"].value);
    }
  };

  const copyFormattedFunc = () => {
    setCopyMsg("Copied Formatted Code");
    navigator.clipboard.writeText(formattedHtml);
  };

  const clearMsg = () => {
    setTimeout(() => {
      setCopyMsg("");
    }, 1500);

    return <div className="copy-msg">{copyMsg}</div>;
  };

  const successLottieOptions = {
    loop: false,
    autoplay: true,
    animationData: successLottie,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const onPublish = async () => {
    await setLocalData();
    if (localStorage.getItem("token")) {
      window.open("/manage?ref=test-html");
    } else {
      window.open("/?ref=test-html");
    }
  };

  return (
    <div className="sandbox">
      {copyMsg ? clearMsg() : false}
      <div className="editor-container">
        <div className="editor-wrapper">
          <div className="editor-controls">
            {vpWidth > 525 ? (
              <div>
                <TTab
                  title="html"
                  onClick={() => setOpenedEditor("html")}
                  openedTab={openedEditor}
                />
                <TTab
                  title="css"
                  onClick={() => setOpenedEditor("css")}
                  openedTab={openedEditor}
                />
                <TTab
                  title="JavaScript"
                  onClick={() => setOpenedEditor("JavaScript")}
                  openedTab={openedEditor}
                />
              </div>
            ) : (
              <select
                className="editor-selector"
                defaultValue="html"
                onChange={(e) => setOpenedEditor(e.target.value)}
              >
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="javaScript">JavaScript</option>
              </select>
            )}
          </div>
          <button className="copy-btn" onClick={copyFunc}>
            <img width="18px" src={copyImage} alt="Copy button" />
          </button>
          <Editor
            className="main-editor"
            theme="vs-dark"
            options={options}
            path={files[fileName].name}
            onMount={handleEditorDidMount}
            defaultLanguage={files[fileName].language}
            defaultValue={files[fileName].value}
            onValidate={handleEditorValidation}
            onChange={async () => {
              const editorValue = editorRef.current.getValue();
              files[fileName].value = editorValue;
              setSrcDoc(`
                <html>
                  <script>${files["script.js"].value}</script>
                  <body>${files["index.html"].value}</body>
                  <style>${files["style.css"].value}</style>
                </html>
              `);
              if (openedEditor === "html") {
                try {
                  const validationErrors = await validateHTML(editorValue);
                  setHtmlErrors(validationErrors);
                  files["index.html"].value = editorValue;
                  formatHtml(editorValue);
                } catch (error) {
                  console.error("HTML validation failed:", error);
                }
              }
            }}
            wordWrap={true}
          />
        </div>
        {isTestHtml ? (
          <div className="renderer-container">
            {htmlErrors.length > 0 ||
            cssErrors.length > 0 ||
            jsErrors.length > 0 ? (
              <ErrorMsg errors={[...cssErrors, ...htmlErrors, ...jsErrors]} />
            ) : (
              <>
                <div className="renderer-header">
                  <h3 className="ms-2 page-title">{pageTitle}</h3>
                  <button
                    type="custom"
                    className="test-btn"
                    onClick={onPublish}
                  >
                    <img src={UploadIcon} alt="upload" height={25} className="me-2" />
                    Publish
                  </button>
                </div>
                <iframe
                  srcDoc={srcDoc}
                  title="output"
                  width="100%"
                  height="90%"
                  ref={iframeRef}
                  onLoad={() => {
                    handleIframeLoad();
                  }}
                />
              </>
            )}
          </div>
        ) : (
          <div className="renderer-container">
            <Tabs
              defaultActiveKey={isValidateHtml ? "validation" : "formattedHtml"}
              id="tab"
            >
              {isValidateHtml ? (
                <Tab eventKey="validation" title="Validation">
                  <button
                    className="copy-btn"
                    onClick={copyFormattedFunc}
                  />
                  <button className="publish-btn test-btn" onClick={onPublish}>
                    Publish
                  </button>
                  {htmlErrors.length > 0 ||
                  cssErrors.length > 0 ||
                  jsErrors.length > 0 ? (
                    <ErrorMsg
                      errors={[...cssErrors, ...htmlErrors, ...jsErrors]}
                    />
                  ) : (
                    <>
                      <Lottie
                        options={successLottieOptions}
                        height={250}
                        width={250}
                      />
                      <h3 className="validate-success">
                        Validation Successful
                      </h3>
                    </>
                  )}
                </Tab>
              ) : (
                <Tab eventKey="formattedHtml" title="Format">
                  {" "}
                  {/* New tab for formatted HTML */}
                  <button className="copy-btn" onClick={copyFormattedFunc}>
                    <img width="18px" src={copyFormatImage} alt="Copy button" />
                  </button>
                  <button className="publish-btn test-btn" onClick={onPublish}>
                    Publish
                  </button>
                  <pre className="pre-format">{formattedHtml}</pre>
                </Tab>
              )}

              <Tab eventKey="preview" title="Preview">
                <button
                  className="copy-btn"
                  onClick={copyFormattedFunc}
                />
                <button className="publish-btn test-btn" onClick={onPublish}>
                  Publish
                </button>
                <iframe
                  srcDoc={srcDoc}
                  title="output"
                  width="100%"
                  height="90%"
                  ref={iframeRef}
                  onLoad={() => {
                    handleIframeLoad();
                  }}
                />
              </Tab>
              {isValidateHtml ? (
                <Tab eventKey="formattedHtml" title="Format">
                  {/* New tab for formatted HTML */}
                  <button className="copy-btn" onClick={copyFormattedFunc}>
                    <img width="18px" src={copyFormatImage} alt="Copy button" />
                  </button>
                  <button className="publish-btn test-btn" onClick={onPublish}>
                    Publish
                  </button>
                  <pre className="pre-format">{formattedHtml}</pre>
                </Tab>
              ) : (
                <Tab eventKey="validation" title="Validation">
                  <button
                    className="copy-btn"
                    onClick={copyFormattedFunc}
                  />
                  <button className="publish-btn test-btn" onClick={onPublish}>
                    Publish
                  </button>
                  {htmlErrors.length > 0 ||
                  cssErrors.length > 0 ||
                  jsErrors.length > 0 ? (
                    <ErrorMsg
                      errors={[...cssErrors, ...htmlErrors, ...jsErrors]}
                    />
                  ) : (
                    <>
                      <Lottie
                        options={successLottieOptions}
                        height={250}
                        width={250}
                      />
                      <h3 className="validate-success">
                        Validation Successful
                      </h3>
                    </>
                  )}
                </Tab>
              )}
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sandbox;
